import React, { useEffect, useMemo, useState } from 'react';
import {
	Grid,
	Stack,
	Typography,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Box,
	ButtonGroup,
	Button,
} from '@mui/material';
import PageHeading from '../../Components/PageHeading';
import { AppColors } from '../../Utils/helper/AppColors';
import MentionProgress from './Components/MentionProgress';
import CultService from '../../Services/CultService';
import toast from 'react-hot-toast';
import { HelperTexts } from '../../Utils/helper/HelperTexts';
import PageLoader from '../../Components/PageLoader';
import { useSelector } from 'react-redux';
import _ from 'lodash';
import { formatAndSortInsights, getColorSettings } from '../../Utils/helper/commonUtils';
import { ExpandIcon } from '../../Components/Icons/Icons';
import DataNotFound from '../../Components/DataNotFound';
import CustomTag from '../../Components/CustomTag';
import SelectCampaign from '../../Components/SelectCampaign';
import CampaignEmptyState from '../../Components/CampaignEmptyState';
import { InsightsTable } from './Components/InsightsTable';

export default function ActionableInsights() {
	const [loading, setLoading] = useState(false);
	const company = useSelector((state) => state?.app?.company);
	const [details, setDetails] = useState(null);
	const [shortDetails, setShortDetails] = useState(null);
	const [selectedInsight, setSelectedInsight] = useState(null); // State to track selected insight
	const [campaignId, setCampaignId] = useState(null);

	const [viewType, setViewType] = useState(0);

	const getDetails = async () => {
		setLoading(true);
		try {
			let res = await CultService.getActionalbleInsigtsV2({ params: { companyId: company?._id, campaignId: campaignId } });
			let sorted = _.orderBy(res?.data?.data, ['reported'], ['desc']);
			setDetails(sorted);
		} catch (err) {
			toast.error(HelperTexts?.SOMETHING_WENT_WRONG);
		} finally {
			setLoading(false);
		}
	};

	const getShortDetails = async () => {
			setLoading(true);
			try {
				let res = await CultService.getShortCampaignDetails({ params: { campaignId : campaignId} });
				console.log('Short Details:', res);
				setShortDetails(res?.data?.data?.actionableInsights);
			} catch (err) {
				toast.error(HelperTexts?.SOMETHING_WENT_WRONG);
			} finally {
				setLoading(false);
			}
		};

	const list = useMemo(() => formatAndSortInsights(details), [details]);

	const handleSelectCampaign = (i, option) => {
		console.log('Selected Campaign iddd:-------', option?.item?._id);
		setCampaignId(option?.item?._id);
	};

	useEffect(() => {
		if (company && campaignId) { getDetails(); getShortDetails(); }
	}, [company, campaignId]);

	useEffect(() => {
		if (list?.length > 0) {
			setSelectedInsight(list[0]); // Set the first insight as selected by default
		}
	}, [list]);

	const localSettingColors = getColorSettings();

	return (
		<Stack sx={{ padding: 2, gap: 1 }}>
			{loading && <PageLoader />}
			<PageHeading title="Actionable Insights" subTitle="Insights into profound employee dynamics" />

			<Stack direction="row" justifyContent="space-between" alignItems="center">
				<SelectCampaign handleSelectCampaign={handleSelectCampaign} width={'488px'} />

				{campaignId && (
					<ButtonGroup>
						<Button variant={viewType === 0 ? 'contained' : 'outlined'} color="info" onClick={() => setViewType(0)}>
							Detailed
						</Button>
						<Button variant={viewType === 1 ? 'contained' : 'outlined'} color="info" onClick={() => setViewType(1)}>
							Summary
						</Button>
					</ButtonGroup>
				)}
			</Stack>

			<Box py={2}>
				{!campaignId && <CampaignEmptyState text="Select a campaign to view campaign insights." />}

				{list?.length === 0 && campaignId ? (
					<Stack
						sx={{ backgroundColor: AppColors.WHITE, height: 'calc(100vh - 180px)', borderRadius: '8px' }}
						alignItems="center"
						justifyContent="center"
						height="100%"
					>
						<DataNotFound />
					</Stack>
				) : viewType === 0 ? (
					<Grid container spacing={{ sm: 2, md: 1 }}>
						{/* Left Side: List of Insights */}
						<Grid
							item
							xs={12}
							sm={12}
							md={3.5}
							sx={{
								height: 'calc(100vh - 180px)',
								overflowY: 'auto',
							}}
						>
							<Stack gap={1}>
								{list?.map((insight, index) => (
									<Stack
										key={index}
										direction="column"
										justifyContent="space-between"
										alignItems="flex-start"
										py={'12px'}
										px={'15px'}
										gap={'10px'}
										height={'80px'}
										sx={(theme) => ({
											backgroundColor: selectedInsight?.name === insight.name ? theme.palette.primary.lightest_shade : AppColors.WHITE,
											borderRadius: '15px',
											border: `1px solid ${
												selectedInsight?.name === insight.name ? theme.palette.primary.lighter_shade : AppColors.BORDER_SECONDARY
											}`,
											cursor: 'pointer',
											// width: '100%',
											'&:hover': { backgroundColor: theme.palette.primary.lightest_shade },
										})}
										onClick={() => setSelectedInsight(insight)} // Set selected insight
									>
										{/* Name and icon */}
										<Stack
											direction="row"
											justifyContent="space-between"
											sx={{ width: '100%' }} // Added width 100% to ensure proper alignment
										>
											<Typography
												textTransform="capitalize"
												sx={(theme) => ({
													color:
														selectedInsight?.name === insight.name
															? theme.palette.primary.darker_primary
															: AppColors.HEADING_TITLE,
													textOverflow: 'ellipsis',
													overflow: 'hidden',
													whiteSpace: 'nowrap',
													maxWidth: '300px',
													minWidth: '200px',
												})}
												fontFamily={'Poppins'}
												fontSize={14}
												fontWeight={600}
												lineHeight={'150%'}
												letterSpacing={'0%'}
											>
												{insight.name}
											</Typography>

											<Stack direction="row" ml={'auto'}>
												<ExpandIcon size={16.34} color={localSettingColors.fontColor} />
											</Stack>
										</Stack>

										{/* Report Tag */}
										<Stack
											direction="row"
											alignItems="center"
											justifyContent="center"
											sx={(theme) => ({
												backgroundColor: selectedInsight?.name === insight.name ? AppColors.WHITE : theme.palette.primary.lightest_shade,
												color:
													selectedInsight?.name === insight.name
														? theme.palette.primary.lightest_shade
														: theme.palette.primary.darker_primary,
												borderRadius: '30px',
												border: `1px solid ${AppColors.BORDER_TERTIARY}`,
												display: 'inline-flex',
												justifyContent: 'center',
												alignItems: 'center',

												px: 2,
												py: '6px',
												marginRight: '10px',
											})}
										>
											<Typography
												fontSize={12}
												fontWeight={500}
												fontFamily={'Poppins'}
												lineHeight={'121%'}
												sx={(theme) => ({ color: theme.palette.primary.darker_primary })}
											>
												Reports: {insight.total_users}
											</Typography>
										</Stack>
									</Stack>
								))}
							</Stack>
						</Grid>

						{/* Right Side: Details of Selected Insight */}
						<Grid item xs={12} sm={12} md={8.5}>
							{selectedInsight ? (
								<Stack gap={1}>
									{/* Container for Topic and Subtopics */}
									<Stack
										direction="column"
										gap={2}
										p={2}
										sx={(theme) => ({
											backgroundColor: AppColors.WHITE,
											borderRadius: 2,
											border: `1px solid ${theme.palette.primary.lighter_shade}`,
											boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',

											height: 'calc(100vh - 180px)',
											overflowY: 'auto',
										})}
									>
										{/* Topic Heading */}
										<Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ marginRight: '15px' }}>
											<Typography
												textTransform="capitalize"
												fontFamily={'Poppins'}
												fontSize={18}
												fontWeight={600}
												lineHeight={'150%'}
												color={AppColors.HEADING_TITLE}
											>
												{selectedInsight.name}
											</Typography>

											{/* Report Tag need cheage in tag fucture */}
											<Stack
												direction="row"
												alignItems="center"
												justifyContent="flex-start"
												sx={(theme) => ({
													backgroundColor: theme.palette.primary.lightest_shade,
													color: theme.palette.primary.darker_primary,
													borderRadius: '30px',
													border: `1px solid ${theme.palette.primary.lighter_shade}`,
													px: 2,
													py: '6px',
													width: 'fit-content',
												})}
											>
												<Typography
													fontWeight={500}
													fontFamily={'Poppins'}
													lineHeight={'121%'}
													fontSize={12}
													sx={(theme) => ({ color: theme.palette.primary.darker_primary })}
												>
													Reports: {selectedInsight.total_users}
												</Typography>
											</Stack>
										</Stack>

										{/* Subtopics */}
										{selectedInsight?.subTopic?.map((sub, subIndex) => (
											<Stack
												key={subIndex}
												direction="column"
												gap={1}
												sx={(theme) => ({
													backgroundColor: AppColors.WHITE,
													borderRadius: '10px',
													border: `1px solid ${theme.palette.primary.light_shade}`,
												})}
											>
												{/* Subtopic Details */}
												<TableContainer sx={{ border: 'none' }}>
													{/* First Header: Subtopic Name and Report Count */}
													<Stack
														direction="row"
														justifyContent="space-between"
														alignItems="center"
														sx={(theme) => ({
															padding: '8px 16px',
															borderRadius: '8px 8px 0 0',
															borderBottom: `1px solid ${theme.palette.primary.light_shade}`,
														})}
													>
														<Typography
															textTransform="capitalize"
															fontWeight={600}
															fontFamily={'Poppins'}
															lineHeight={'150%'}
															fontSize={16}
														>
															{sub?.name}
														</Typography>

														<Stack
															direction="row"
															alignItems="center"
															justifyContent="flex-start"
															sx={(theme) => ({
																backgroundColor: theme.palette.primary.lightest_shade,
																color: theme.palette.primary.darker_primary,
																borderRadius: '30px',
																border: `1px solid ${theme.palette.primary.lighter_shade}`,
																px: 2,
																py: '6px',
																width: 'fit-content',
															})}
														>
															<Typography
																fontWeight={500}
																fontFamily={'Poppins'}
																lineHeight={'121%'}
																fontSize={12}
																sx={(theme) => ({ color: theme.palette.primary.darker_primary })}
															>
																Reports: {sub?.total_users}
															</Typography>
														</Stack>
													</Stack>

													{/* Table */}
													<Table sx={{ width: '96%', margin: '0 auto' }}>
														{/* Second Header: Column Headers */}
														<TableHead
															sx={(theme) => ({
																borderBottom: `1px solid ${theme.palette.primary.lighter_shade}`,
																'& .MuiTableCell-root': {
																	paddingLeft: 0,
																	paddingY: '8px',
																	fontWeight: 600,
																	fontFamily: 'Poppins',
																	lineHeight: '150%',
																	fontSize: 14,
																	color: AppColors.HEADING_TITLE2,
																},
															})}
														>
															<TableRow>
																<TableCell width="30.50%">Insights</TableCell>
																<TableCell width="69.50%">Recommended Action</TableCell>
															</TableRow>
														</TableHead>

														{/* Table Body */}
														<TableBody sx={{ '& .MuiTableCell-root': { paddingLeft: 0, verticalAlign: 'top' } }}>
															{sub?.cluster?.map((mention, mentionIndex) => (
																<TableRow key={mentionIndex}>
																	<TableCell>
																		<Typography
																			sx={{
																				paddingLeft: 0,
																				fontWeight: 400,
																				fontFamily: 'Poppins',
																				lineHeight: '150%',
																				fontSize: '14px',
																				color: AppColors.HEADING_TITLE3,
																			}}
																		>
																			{mention?.cluster?.issue}
																		</Typography>
																	</TableCell>
																	<TableCell>
																		<Typography
																			sx={{
																				paddingLeft: 0,
																				fontWeight: 400,
																				fontFamily: 'Poppins',
																				lineHeight: '150%',
																				fontSize: '14px',
																				color: AppColors.HEADING_TITLE3,
																			}}
																		>
																			{mention?.cluster?.solution}
																		</Typography>
																	</TableCell>
																</TableRow>
															))}
														</TableBody>
													</Table>
												</TableContainer>
											</Stack>
										))}
									</Stack>
								</Stack>
							) : (
								<Stack alignItems="center" justifyContent="center" height="100%">
									<Typography variant="h6" color={AppColors.TEXT_SECONDARY}>
										{/* Select an insight to view details */}
									</Typography>
								</Stack>
							)}
						</Grid>
					</Grid>
				) : (
					<InsightsTable
						data={shortDetails}
					/>
				)}
			</Box>
		</Stack>
	);
}
